import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface SurveyQuestion {
  id: string;
  type: 'rating' | 'text';
  min?: number;
  max?: number;
  text: string;
}

interface SurveyData {
  permitReference: string;
  permitType: string;
  questions: SurveyQuestion[];
}

class SurveyHttpError extends Error {
  constructor(public status: number) {
    super(`HTTP ${status}`);
  }
}

async function fetchSurvey(token: string): Promise<SurveyData> {
  const res = await fetch(`${API_BASE}/api/v1/survey/${token}`);
  if (!res.ok) throw new SurveyHttpError(res.status);
  return res.json();
}

async function submitSurvey(
  token: string,
  responses: { questionId: string; answer: string | number }[],
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/v1/survey/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ responses }),
  });
  if (!res.ok) throw new SurveyHttpError(res.status);
}

export const Route = createFileRoute('/survey/$token')({
  component: SurveyPage,
});

function buildSurveySchema(questions: SurveyQuestion[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const q of questions) {
    shape[q.id] =
      q.type === 'rating' ? z.string().min(1, 'Please select a rating') : z.string().default('');
  }
  return z.object(shape);
}

function SurveyForm({ survey, token }: { survey: SurveyData; token: string }) {
  const schema = useMemo(() => buildSurveySchema(survey.questions), [survey.questions]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Record<string, string>>({
    resolver: zodResolver(schema),
    defaultValues: Object.fromEntries(survey.questions.map((q) => [q.id, ''])),
  });

  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (answers: Record<string, string>) => {
      const responses = survey.questions.map((q) => ({
        questionId: q.id,
        answer: q.type === 'rating' ? Number(answers[q.id]) : (answers[q.id] ?? ''),
      }));
      return submitSurvey(token, responses);
    },
    onError: (err) => {
      if (err instanceof SurveyHttpError && err.status === 410) {
        setAlreadySubmitted(true);
      }
    },
  });

  if (alreadySubmitted) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-gray-800">Already Submitted</h1>
          <p className="mt-2 text-gray-500">You have already submitted this survey. Thank you!</p>
        </div>
      </main>
    );
  }

  if (mutation.isSuccess) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-green-700">Thank You!</h1>
          <p className="mt-2 text-gray-600">
            Thank you for your feedback! Your response has been recorded.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 shadow-sm">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Permit Satisfaction Survey</h1>
          <p className="mt-1 text-sm text-gray-500">
            {survey.permitType} — {survey.permitReference}
          </p>
        </header>

        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
          {survey.questions.map((q) => {
            const currentVal = watch(q.id);
            return (
              <div key={q.id}>
                <p className="mb-2 font-medium text-gray-800">{q.text}</p>

                {q.type === 'rating' && (
                  <>
                    <div className="flex gap-2">
                      {Array.from(
                        { length: (q.max ?? 5) - (q.min ?? 1) + 1 },
                        (_, i) => i + (q.min ?? 1),
                      ).map((val) => (
                        <label
                          key={val}
                          className="flex flex-col items-center gap-1 cursor-pointer"
                        >
                          <input type="radio" value={val} className="sr-only" {...register(q.id)} />
                          <span
                            className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors ${
                              Number(currentVal) === val
                                ? 'border-brand-500 bg-brand-500 text-white'
                                : 'border-gray-300 text-gray-600 hover:border-brand-400'
                            }`}
                          >
                            {val}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors[q.id] && (
                      <p className="mt-1 text-xs text-red-600">{errors[q.id]?.message as string}</p>
                    )}
                  </>
                )}

                {q.type === 'text' && (
                  <Textarea rows={3} placeholder="Your comments (optional)" {...register(q.id)} />
                )}
              </div>
            );
          })}

          {mutation.isError &&
            !(mutation.error instanceof SurveyHttpError && mutation.error.status === 410) && (
              <p className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600">
                Submission failed. Please try again.
              </p>
            )}

          <Button type="submit" variant="primary" className="w-full" disabled={mutation.isPending}>
            {mutation.isPending ? 'Submitting…' : 'Submit Feedback'}
          </Button>
        </form>
      </div>
    </main>
  );
}

function SurveyPage() {
  const { token } = Route.useParams();

  const {
    data: survey,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['survey', token],
    queryFn: () => fetchSurvey(token),
    retry: false,
  });

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-gray-500">Loading survey…</p>
      </main>
    );
  }

  if (error instanceof SurveyHttpError && error.status === 404) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-gray-800">Survey Not Found</h1>
          <p className="mt-2 text-gray-500">This survey link is invalid or has expired.</p>
        </div>
      </main>
    );
  }

  if (error instanceof SurveyHttpError && error.status === 410) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-gray-800">Already Submitted</h1>
          <p className="mt-2 text-gray-500">You have already submitted this survey. Thank you!</p>
        </div>
      </main>
    );
  }

  if (error || !survey) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-red-700">Error</h1>
          <p className="mt-2 text-gray-500">Something went wrong. Please try again later.</p>
        </div>
      </main>
    );
  }

  return <SurveyForm survey={survey} token={token} />;
}
